if (!customElements.get('product-series-switcher')) {
  class ProductSeriesSwitcher extends HTMLElement {
    connectedCallback() {
      this.initCarousel();
    }

    disconnectedCallback() {
      this.destroyCarousel();
    }

    initCarousel() {
      this.list = this.querySelector('.product-series-switcher__list');
      this.prevBtn = this.querySelector('[data-series-nav="prev"]');
      this.nextBtn = this.querySelector('[data-series-nav="next"]');
      if (!this.list) return;

      this.list.addEventListener('scroll', this.updateNav, { passive: true });
      this.list.addEventListener('scroll', this.onInfiniteScroll, { passive: true });
      this.prevBtn?.addEventListener('click', this.scrollPrev);
      this.nextBtn?.addEventListener('click', this.scrollNext);

      this.resizeObserver = new ResizeObserver(() => {
        if (this._infiniteReady && !this._didInitialJump) {
          this.jumpToRealSet();
          this._didInitialJump = true;
        }
        this.normalizeInfiniteScroll();
        this.updateNav();
      });
      this.resizeObserver.observe(this.list);

      this.setupInfiniteScroll();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.jumpToRealSet();
          this._didInitialJump = true;
          this.updateNav();
        });
      });
    }

    destroyCarousel() {
      this.list?.removeEventListener('scroll', this.updateNav);
      this.list?.removeEventListener('scroll', this.onInfiniteScroll);
      this.prevBtn?.removeEventListener('click', this.scrollPrev);
      this.nextBtn?.removeEventListener('click', this.scrollNext);
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
    }

    setupInfiniteScroll() {
      if (!this.list || this.list.dataset.infinite === 'true') {
        this._infiniteReady = this.list?.dataset.infinite === 'true';
        this._setCount = parseInt(this.list?.dataset.setCount || '0', 10);
        this._didInitialJump = true;
        return;
      }

      const items = [...this.list.querySelectorAll(':scope > .product-series-switcher__list-item')];
      if (items.length < 2) {
        this._infiniteReady = false;
        return;
      }

      const prependFrag = document.createDocumentFragment();
      const appendFrag = document.createDocumentFragment();

      items.forEach((item) => {
        const pre = item.cloneNode(true);
        const post = item.cloneNode(true);
        [pre, post].forEach((clone) => {
          clone.dataset.clone = 'true';
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('[aria-current]').forEach((el) => el.removeAttribute('aria-current'));
        });
        prependFrag.appendChild(pre);
        appendFrag.appendChild(post);
      });

      this.list.prepend(prependFrag);
      this.list.append(appendFrag);
      this.list.dataset.infinite = 'true';
      this.list.dataset.setCount = String(items.length);
      this._infiniteReady = true;
      this._setCount = items.length;
      this._jumping = false;
      this._didInitialJump = false;
    }

    getSetWidth() {
      if (!this.list || !this._infiniteReady) return 0;
      return this.list.scrollWidth / 3;
    }

    jumpToRealSet() {
      if (!this.list || !this._infiniteReady) return;
      const setWidth = this.getSetWidth();
      if (!setWidth) return;
      this._jumping = true;
      this.list.style.scrollBehavior = 'auto';
      this.list.scrollLeft = setWidth;
      this._jumping = false;
    }

    normalizeInfiniteScroll() {
      if (!this.list || !this._infiniteReady || this._jumping) return;
      const setWidth = this.getSetWidth();
      if (!setWidth) return;

      const scrollLeft = this.list.scrollLeft;
      if (scrollLeft < setWidth) {
        this._jumping = true;
        this.list.style.scrollBehavior = 'auto';
        this.list.scrollLeft = scrollLeft + setWidth;
        this._jumping = false;
      } else if (scrollLeft >= setWidth * 2) {
        this._jumping = true;
        this.list.style.scrollBehavior = 'auto';
        this.list.scrollLeft = scrollLeft - setWidth;
        this._jumping = false;
      }
    }

    onInfiniteScroll = () => {
      this.normalizeInfiniteScroll();
    };

    getScrollAmount() {
      const item = this.list?.querySelector('.product-series-switcher__list-item');
      if (!item) return this.list?.clientWidth * 0.75 || 0;
      const styles = window.getComputedStyle(this.list);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    scrollPrev = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!this.list) return;
      this.list.scrollBy({ left: -this.getScrollAmount() * 2, behavior: 'smooth' });
    };

    scrollNext = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!this.list) return;
      this.list.scrollBy({ left: this.getScrollAmount() * 2, behavior: 'smooth' });
    };

    updateNav = () => {
      if (!this.list) return;

      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      const canScroll = maxScroll > 2;

      if (this._infiniteReady) {
        if (this.prevBtn) this.prevBtn.hidden = !canScroll;
        if (this.nextBtn) this.nextBtn.hidden = !canScroll;
        return;
      }

      const atStart = this.list.scrollLeft <= 2;
      const atEnd = this.list.scrollLeft >= maxScroll - 2;

      if (this.prevBtn) this.prevBtn.hidden = !canScroll || atStart;
      if (this.nextBtn) this.nextBtn.hidden = !canScroll || atEnd;
    };

    static loading = false;

    static handleLinkClick(event) {
      const link = event.target.closest('product-series-switcher [data-product-url]');
      if (!link) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();

      if (link.classList.contains('is-active')) return;

      const url = link.getAttribute('data-product-url');
      if (!url) return;

      ProductSeriesSwitcher.navigate(url, true);
    }

    static isExecutableScript(scriptTag) {
      const type = (scriptTag.getAttribute('type') || 'text/javascript').trim().toLowerCase();
      return !type || type === 'text/javascript' || type === 'module' || type.endsWith('javascript');
    }

    static reinjectScripts(container, skipRoot) {
      container.querySelectorAll('script').forEach((oldScriptTag) => {
        if (skipRoot?.contains(oldScriptTag)) return;

        if (!ProductSeriesSwitcher.isExecutableScript(oldScriptTag)) return;

        // External scripts are already registered globally; re-fetching PayPal,
        // app modules, etc. can throw and trigger a full-page fallback reload.
        if (oldScriptTag.src) {
          oldScriptTag.remove();
          return;
        }

        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    static setLoading(section, isLoading) {
      if (!section) return;
      const product = section.querySelector('.product');
      const breadcrumb = section.querySelector('.bear-breadcrumb');
      [product, breadcrumb].forEach((el) => {
        if (!el) return;
        el.classList.toggle('product-series-switcher--loading-target', isLoading);
      });
      section.classList.toggle('product-series-switcher--loading', isLoading);
    }

    static getStylesheetKey(link) {
      const href = link.getAttribute('href') || '';
      try {
        const url = new URL(href, window.location.origin);
        return url.pathname;
      } catch (error) {
        return href;
      }
    }

    static removeStaleProductModals(section) {
      const sectionId = section?.dataset?.section;
      if (!sectionId) return;

      document.querySelectorAll(`product-modal#ProductModal-${sectionId}`).forEach((modal) => {
        modal.remove();
      });
    }

    static replaceSectionKeepingSwitcher(section, newSection, preservedSwitcher) {
      newSection.querySelector('product-series-switcher')?.remove();
      ProductSeriesSwitcher.removeStaleProductModals(section);

      const list = preservedSwitcher?.querySelector('.product-series-switcher__list');
      const savedScrollLeft = list ? list.scrollLeft : 0;

      const existingStyleKeys = new Set(
        [...section.querySelectorAll('link[rel="stylesheet"]')].map((link) =>
          ProductSeriesSwitcher.getStylesheetKey(link)
        )
      );

      [...section.children].forEach((child) => {
        if (child === preservedSwitcher) return;
        if (child.matches?.('link[rel="stylesheet"]')) return;
        child.remove();
      });

      while (newSection.firstChild) {
        const node = newSection.firstChild;

        if (node.nodeType === 1 && node.matches('link[rel="stylesheet"]')) {
          const key = ProductSeriesSwitcher.getStylesheetKey(node);
          if (key && existingStyleKeys.has(key)) {
            const existing = [...section.querySelectorAll('link[rel="stylesheet"]')].find(
              (link) => ProductSeriesSwitcher.getStylesheetKey(link) === key
            );
            const nextHref = node.getAttribute('href');
            if (existing && nextHref && existing.getAttribute('href') !== nextHref) {
              existing.setAttribute('href', nextHref);
            }
            node.remove();
            continue;
          }
          if (key) existingStyleKeys.add(key);
        }

        section.appendChild(node);
      }

      if (preservedSwitcher) {
        const breadcrumb = section.querySelector('.bear-breadcrumb');
        if (breadcrumb && preservedSwitcher.nextElementSibling !== breadcrumb) {
          breadcrumb.before(preservedSwitcher);
        } else if (!breadcrumb) {
          const product = section.querySelector('.product');
          if (product && preservedSwitcher.nextElementSibling !== product) {
            product.before(preservedSwitcher);
          } else if (!product) {
            section.prepend(preservedSwitcher);
          }
        }
      }

      ProductSeriesSwitcher.reinjectScripts(section, preservedSwitcher);

      const restoreScroll = () => {
        if (!list) return;
        list.style.scrollBehavior = 'auto';
        list.scrollLeft = savedScrollLeft;
      };
      restoreScroll();
      requestAnimationFrame(() => {
        restoreScroll();
        requestAnimationFrame(restoreScroll);
      });
    }

    static judgeMeModuleLoads = 0;

    // The Judge.me review widget lives in its own section below the product
    // section, so navigate() would leave it showing the previous product's
    // reviews. Swap in the widget from the fetched page, re-run its inline
    // review-data script, then re-import the widget module so it hydrates the
    // new node (the module only runs once per URL, hence the cache-bust).
    static syncJudgeMeReviews(newDoc) {
      const currentWidget = document.querySelector('#judgeme_product_reviews');
      const newWidget = newDoc.querySelector('#judgeme_product_reviews');
      if (!currentWidget && !newWidget) return;

      if (!newWidget) {
        currentWidget?.closest('.shopify-section')?.remove();
        return;
      }

      if (currentWidget) {
        const currentSection = currentWidget.closest('.shopify-section');
        const newSection = newWidget.closest('.shopify-section');
        if (currentSection && newSection) {
          currentSection.replaceWith(newSection);
        } else {
          currentWidget.replaceWith(newWidget);
        }
      } else {
        document.querySelector('main')?.appendChild(newWidget.closest('.shopify-section') || newWidget);
      }

      ProductSeriesSwitcher.reinjectScripts(newWidget.closest('.shopify-section') || newWidget, null);
      ProductSeriesSwitcher.bootJudgeMeWidget(newWidget);
    }

    static bootJudgeMeWidget(widget) {
      const entryPoint = widget.dataset.entryPoint;
      const baseURL = window.jdgm?.CDN_BASE_URL;
      if (!entryPoint || !baseURL) return;

      const script = document.createElement('script');
      script.type = 'module';
      script.src = `${baseURL}${entryPoint}?series-switch=${++ProductSeriesSwitcher.judgeMeModuleLoads}`;
      script.onload = () => {
        ProductSeriesSwitcher.syncJudgeMePreviewBadge(
          document.querySelector('section[id^="MainProduct-"]')
        );
      };
      document.head.appendChild(script);
    }

    // Preview badge stars live in the main product section. After a series
    // switch the HTML is replaced but Judge.me only wires click-to-scroll on
    // first page load. Its handler also compares badge data-id against a
    // cached review widget pointer, so refresh that cache before re-setup.
    static syncJudgeMePreviewBadge(section) {
      const scope = section || document.querySelector('section[id^="MainProduct-"]');
      if (!scope) return;

      ProductSeriesSwitcher.bindPreviewBadgeScroll(scope);

      const jdgm = window.jdgm;
      if (!jdgm?.customizeBadges || typeof jdgm.$ !== 'function') return;

      scope.querySelectorAll('.jdgm-preview-badge').forEach((badge) => {
        badge.classList.remove('jdgm--done-setup');
      });

      if (jdgm.caches) {
        jdgm.caches.$revWidgets = jdgm.$(
          '#judgeme_product_reviews, .jdgm-review-widget, [data-widget="review"]'
        );
        jdgm.caches.$prevBadges = jdgm.$('.jdgm-preview-badge');
      }

      const badges = scope.querySelectorAll('.jdgm-preview-badge:not(.jdgm--done-setup)');
      if (badges.length) {
        jdgm.customizeBadges(jdgm.$(badges));
      }
    }

    static bindPreviewBadgeScroll(section) {
      section.querySelectorAll('.jdgm-preview-badge').forEach((badge) => {
        if (badge.dataset.seriesScrollBound === 'true') return;
        badge.dataset.seriesScrollBound = 'true';

        badge.addEventListener('click', (event) => {
          if (
            !event.target.closest(
              '.jdgm-prev-badge__stars, .jdgm-prev-badge__text, .jdgm-prev-badge, .jdgm-star'
            )
          ) {
            return;
          }

          const reviews = document.getElementById('judgeme_product_reviews');
          if (!reviews) return;

          event.preventDefault();

          if (typeof window.jdgm?._openTabAndScrollToRevWidget === 'function') {
            try {
              window.jdgm._openTabAndScrollToRevWidget();
              return;
            } catch (error) {
              // fall through to native scroll
            }
          }

          reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    // The related-products section bakes the product id into its data-url at
    // render time, so it keeps recommending the previous product after a
    // series switch. Re-create the element in the live document (never adopt
    // nodes from the parsed fetch — that breaks custom element upgrades) and
    // fetch recommendations immediately, because the block is usually already
    // in the viewport and a fresh IntersectionObserver would not re-fire.
    static async loadProductRecommendations(element) {
      if (!element?.dataset?.url) return;

      element.classList.remove('product-recommendations--loaded');
      element.innerHTML = '';

      const response = await fetch(element.dataset.url);
      if (!response.ok) throw new Error(`Failed to fetch recommendations: ${response.status}`);

      const text = await response.text();
      const html = document.createElement('div');
      html.innerHTML = text;
      const recommendations = html.querySelector('product-recommendations');

      if (recommendations?.innerHTML.trim()) {
        element.innerHTML = recommendations.innerHTML;
        if (typeof initProductColorSwatches === 'function') initProductColorSwatches(element);
      }

      if (html.querySelector('.grid__item')) {
        element.classList.add('product-recommendations--loaded');
      }
    }

    static getSizeChartPopup(doc) {
      return (
        doc.getElementById('ProductSizeChartPopup') ||
        doc.querySelector('.size-chart-popup:not([data-quick-add-size-chart])')
      );
    }

    static getSizeChartInlineScript(doc) {
      const popup = ProductSeriesSwitcher.getSizeChartPopup(doc);
      if (!popup) return null;

      let node = popup.nextElementSibling;
      while (node) {
        if (node.matches?.('script[src*="size-chart-pagination"]')) {
          node = node.nextElementSibling;
          continue;
        }
        if (node.tagName === 'SCRIPT' && !node.src) {
          return node.textContent;
        }
        break;
      }
      return null;
    }

    static removeSizeChartCompanionNodes(popup) {
      if (!popup) return;

      let node = popup.nextElementSibling;
      while (node) {
        const next = node.nextElementSibling;
        if (node.matches?.('script[src*="size-chart-pagination"]') || (node.tagName === 'SCRIPT' && !node.src)) {
          const isInline = node.tagName === 'SCRIPT' && !node.src;
          node.remove();
          if (isInline) break;
        } else {
          break;
        }
        node = next;
      }
    }

    static runInlineScript(code) {
      const trimmed = code.trim();
      const wrapped = trimmed.startsWith('(function')
        ? trimmed
        : `(function(){\n${code}\n})();`;

      const script = document.createElement('script');
      script.textContent = wrapped;
      document.body.appendChild(script);
      script.remove();
    }

    // Size chart markup is rendered after </section> in main-product.liquid,
    // so replaceSectionKeepingSwitcher never touches it. Re-import the popup
    // for the new product and re-run its inline init so .size-chart-button
    // click handlers bind to the freshly swapped variant picker button.
    static syncSizeChartPopup(newDoc) {
      const currentPopup = ProductSeriesSwitcher.getSizeChartPopup(document);
      const newPopup = ProductSeriesSwitcher.getSizeChartPopup(newDoc);

      if (!newPopup) {
        if (currentPopup) {
          ProductSeriesSwitcher.removeSizeChartCompanionNodes(currentPopup);
          currentPopup.remove();
        }
        return;
      }

      const importedPopup = document.importNode(newPopup, true);

      if (currentPopup) {
        ProductSeriesSwitcher.removeSizeChartCompanionNodes(currentPopup);
        currentPopup.replaceWith(importedPopup);
      } else {
        document.querySelector('section[id^="MainProduct-"]')?.insertAdjacentElement('afterend', importedPopup);
      }

      const inlineScript = ProductSeriesSwitcher.getSizeChartInlineScript(newDoc);
      if (inlineScript) {
        try {
          ProductSeriesSwitcher.runInlineScript(inlineScript);
        } catch (error) {
          console.error('[product-series-switcher] Failed to init size chart popup', error);
        }
      }
    }

    static syncRelatedProducts(newDoc) {
      const current = document.querySelector('product-recommendations.related-products');
      const upcoming = newDoc.querySelector('product-recommendations.related-products');
      if (!current || !upcoming) return;

      const fresh = document.createElement('product-recommendations');
      Array.from(upcoming.attributes).forEach((attribute) => {
        fresh.setAttribute(attribute.name, attribute.value);
      });
      fresh.dataset.manualLoad = 'true';

      current.replaceWith(fresh);
      return ProductSeriesSwitcher.loadProductRecommendations(fresh);
    }

    static updateActiveItem(switcher, url) {
      if (!switcher || !url) return;

      const targetPath = new URL(url, window.location.origin).pathname;

      switcher.querySelectorAll('[data-product-url]').forEach((link) => {
        const linkPath = new URL(link.getAttribute('data-product-url'), window.location.origin).pathname;
        const isActive = linkPath === targetPath;
        const isClone = Boolean(link.closest('[data-clone="true"]'));
        link.classList.toggle('is-active', isActive);
        if (isActive && !isClone) {
          link.setAttribute('aria-current', 'page');
        } else {
          link.removeAttribute('aria-current');
        }
      });

      switcher.updateNav?.();
    }

    static async navigate(url, push) {
      const section = document.querySelector('section[id^="MainProduct-"]');
      if (!section || ProductSeriesSwitcher.loading) return;

      ProductSeriesSwitcher.loading = true;
      ProductSeriesSwitcher.setLoading(section, true);

      const preservedSwitcher = section.querySelector('product-series-switcher');

      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Failed to fetch product: ${response.status}`);

        const responseText = await response.text();
        const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const newSection = responseHTML.querySelector('section[id^="MainProduct-"]');
        if (!newSection) throw new Error('Main product section not found');

        ProductSeriesSwitcher.replaceSectionKeepingSwitcher(section, newSection, preservedSwitcher);
        ProductSeriesSwitcher.updateActiveItem(preservedSwitcher, url);

        try {
          ProductSeriesSwitcher.syncSizeChartPopup(responseHTML);
        } catch (error) {
          console.error('[product-series-switcher] Failed to sync size chart popup', error);
        }

        try {
          ProductSeriesSwitcher.syncJudgeMeReviews(responseHTML);
          ProductSeriesSwitcher.syncJudgeMePreviewBadge(
            document.querySelector('section[id^="MainProduct-"]')
          );
        } catch (error) {
          console.error('[product-series-switcher] Failed to sync Judge.me reviews', error);
        }

        try {
          await ProductSeriesSwitcher.syncRelatedProducts(responseHTML);
        } catch (error) {
          console.error('[product-series-switcher] Failed to sync related products', error);
        }

        const titleEl = responseHTML.querySelector('title');
        if (titleEl) document.title = titleEl.textContent;

        if (push) {
          history.pushState({ seriesSwitcher: true }, '', url);
        }

        try {
          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
        } catch (error) {
          console.error('[product-series-switcher] Failed to init payment buttons', error);
        }

        try {
          if (window.ProductModel) window.ProductModel.loadShopifyXR();
        } catch (error) {
          console.error('[product-series-switcher] Failed to load 3D model viewer', error);
        }
      } catch (error) {
        console.error('[product-series-switcher] Navigation failed, falling back to full page load', error);
        window.location.href = url;
      } finally {
        ProductSeriesSwitcher.loading = false;
        const currentSection = document.querySelector('section[id^="MainProduct-"]');
        ProductSeriesSwitcher.setLoading(currentSection, false);
      }
    }
  }

  customElements.define('product-series-switcher', ProductSeriesSwitcher);

  if (!window.__productSeriesSwitcherClickBound) {
    window.__productSeriesSwitcherClickBound = true;
    document.addEventListener('click', ProductSeriesSwitcher.handleLinkClick, true);
  }

  if (!window.__productSeriesSwitcherPopstateBound) {
    window.__productSeriesSwitcherPopstateBound = true;
    window.addEventListener('popstate', () => {
      if (!window.location.pathname.includes('/products/')) {
        window.location.reload();
        return;
      }
      if (!document.querySelector('section[id^="MainProduct-"]')) return;
      ProductSeriesSwitcher.navigate(window.location.href, false);
    });
  }
}
